import { getControl } from '@shared/helpers/form.helper';
import { map, take } from 'rxjs';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { listenerSessionStart$ } from '@shared/operators/listener-session';
import { FormGroup } from '@angular/forms';
import { DataEsPrecState } from './models/base-sociale-listeners.model';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { onlyNumberRegex } from '@shared/constants/regex.const';
import { SmartToastService } from '@shared/services/smart-toast.service';
import { baseSocialeEserciziPrecedentiFlash } from './base-sociale-toast.listeners';
import { toNum } from '@shared/helpers/math.helper';
import { isAfter } from 'date-fns';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { cloneDeep } from 'lodash-es';
import { revisione53DatiSecondariSelectors } from '../../../store/selectors/dati-secondari.selectors';
import { getBaseSocialeControl } from '../validators/helpers/base-sociale-form.helper';

export const setEserciziPrecedentiListeners = (form: FormGroup, store: Store<Revisione53State>, smartToast: SmartToastService, dataEsPrecState: DataEsPrecState): void => {
    manageMyDataEsPrec('my_Data1EsPrec', 'Primo', form, store, smartToast, dataEsPrecState);
    manageMyDataEsPrec('my_Data2EsPrec', 'Secondo', form, store, smartToast, dataEsPrecState);
};

const manageMyDataEsPrec = (
    dataPath: string,
    suffix: 'Primo' | 'Secondo',
    form: FormGroup,
    store: Store<Revisione53State>,
    smartToast: SmartToastService,
    dataEsPrecState: DataEsPrecState
): void => {
    const updatePreviousData = (value: string) => {
        if (suffix === 'Primo') {
            dataEsPrecState.my_Data1EsPrecPreviousValue = value;
        } else {
            dataEsPrecState.my_Data2EsPrecPreviousValue = value;
        }
    };

    const buildName = (name: string) => `${name}${suffix}`;

    // Each manageMyDataEsPrec call tracks its own previous ID independently via closure,
    // so 'Primo' and 'Secondo' cannot interfere with each other during a verbale switch.
    let previousIdAssegnazione: string = getControl(form, 'my_IdAssegnazione').value;

    const dataControl = getBaseSocialeControl(form, dataPath);
    // Nuovo verbale senza F5: il form è già ripopolato, quindi lo stato locale si riallinea al verbale corrente
    // (altrimenti la prima modifica utente verrebbe scambiata per un cambio verbale).
    listenerSessionStart$.subscribe(() => {
        previousIdAssegnazione = getControl(form, 'my_IdAssegnazione').value;
        updatePreviousData(dataControl.value);
    });
    dataControl.valueChanges
        .pipe(
            withChangeDebounced(dataControl, 1000),
            map((my_Data: string) => ({ my_Data, currentIdAssegnazione: getControl(form, 'my_IdAssegnazione').value }))
        )
        .subscribe(({ my_Data, currentIdAssegnazione }) => {
        // If my_IdAssegnazione changed, this emission was triggered by a verbale switch, not user input.
        // populateForm() already patched the correct values — skip clear+reload.
        if (previousIdAssegnazione !== currentIdAssegnazione) {
            previousIdAssegnazione = currentIdAssegnazione;
            updatePreviousData(my_Data);
            return;
        }

        // Read inside subscribe so the comparison always uses the latest accepted value
        const previousData = suffix === 'Primo' ? dataEsPrecState.my_Data1EsPrecPreviousValue : dataEsPrecState.my_Data2EsPrecPreviousValue;

        if (previousData !== my_Data) {
            if (isNilOrEmpty(my_Data)) {
                updatePreviousData('');
            } else {
                const ggmmValue: string = getBaseSocialeControl(form, 'my_ChiusuraEsercizio').value;

                if (my_Data.length !== 4 || !onlyNumberRegex.test(my_Data)) {
                    getBaseSocialeControl(form, dataPath).setValue(previousData, { emitEvent: false });
                    baseSocialeEserciziPrecedentiFlash.annoNonValidoFormato(smartToast);
                    return;
                }

                const parts = (ggmmValue ?? '').split(/[\/\-]/);
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1;
                const year = toNum(my_Data);
                const parsedDate = new Date(year, month, day);
                const isValidDate = !isNaN(parsedDate.getTime()) && parsedDate.getFullYear() === year && parsedDate.getMonth() === month && parsedDate.getDate() === day;

                if (!isValidDate) {
                    getBaseSocialeControl(form, dataPath).setValue(previousData, { emitEvent: false });
                    baseSocialeEserciziPrecedentiFlash.annoNonValido(smartToast);
                    return;
                }

                if (isAfter(parsedDate, new Date())) {
                    getBaseSocialeControl(form, dataPath).setValue(previousData, { emitEvent: false });
                    baseSocialeEserciziPrecedentiFlash.dataFutura(smartToast);
                    return;
                }

                updatePreviousData(my_Data);
            }
        }

        // Always clear soci, lav, and (if Primo) Data2EsPrec — matching InfoPath's unconditional clear
        const my_SociFisCoop = getBaseSocialeControl(form, buildName('my_SociFisCoop'));
        const my_SociGiuCoop = getBaseSocialeControl(form, buildName('my_SociGiuCoop'));
        const my_SociFisFin = getBaseSocialeControl(form, buildName('my_SociFisFin'));
        const my_SociGiuFin = getBaseSocialeControl(form, buildName('my_SociGiuFin'));
        const my_SociSpec = getBaseSocialeControl(form, buildName('my_SociSpec'));

        my_SociFisCoop.setValue('', { emitEvent: false });
        my_SociGiuCoop.setValue('', { emitEvent: false });
        my_SociFisFin.setValue('', { emitEvent: false });
        my_SociGiuFin.setValue('', { emitEvent: false });
        my_SociSpec.setValue('', { emitEvent: false });

        const my_UnitaSociUtil = getBaseSocialeControl(form, buildName('my_UnitaSociUtil'));
        const my_UnitaNonSociUtil = getBaseSocialeControl(form, buildName('my_UnitaNonSociUtil'));
        const my_NumLavSub = getBaseSocialeControl(form, buildName('my_NumLavSub'));
        const my_NumLavSubTempoDeterminato = getBaseSocialeControl(form, buildName('my_NumLavSubTempoDeterminato'));
        const my_NumLavParaSub = getBaseSocialeControl(form, buildName('my_NumLavParaSub'));
        const my_NumLavAut = getBaseSocialeControl(form, buildName('my_NumLavAut'));
        const my_NumLavAltro = getBaseSocialeControl(form, buildName('my_NumLavAltro'));

        my_UnitaSociUtil.setValue('', { emitEvent: false });
        my_UnitaNonSociUtil.setValue('', { emitEvent: false });
        my_NumLavSub.setValue('', { emitEvent: false });
        my_NumLavSubTempoDeterminato.setValue('', { emitEvent: false });
        my_NumLavParaSub.setValue('', { emitEvent: false });
        my_NumLavAut.setValue('', { emitEvent: false });
        my_NumLavAltro.setValue('', { emitEvent: false });

        const data2EsPrecControl = suffix === 'Primo' ? getBaseSocialeControl(form, 'my_Data2EsPrec') : null;

        // If date is empty: persist the cleared state to store and exit — no DB reload
        if (isNilOrEmpty(my_Data)) {
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone = cloneDeep(myVerbale);
                const sez = myVerbaleClone.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale;
                sez[buildName('my_SociFisCoop')] = '';
                sez[buildName('my_SociGiuCoop')] = '';
                sez[buildName('my_SociFisFin')] = '';
                sez[buildName('my_SociGiuFin')] = '';
                sez[buildName('my_SociSpec')] = '';
                sez[buildName('my_UnitaSociUtil')] = '';
                sez[buildName('my_UnitaNonSociUtil')] = '';
                sez[buildName('my_NumLavSub')] = '';
                sez[buildName('my_NumLavSubTempoDeterminato')] = '';
                sez[buildName('my_NumLavParaSub')] = '';
                sez[buildName('my_NumLavAut')] = '';
                sez[buildName('my_NumLavAltro')] = '';
                if (data2EsPrecControl) {
                    data2EsPrecControl.setValue('', { emitEvent: false });
                    myVerbaleClone.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_Data2EsPrec = '';
                }
                return myVerbaleClone;
            });
            return;
        }

        const idAssegnazione: string = getControl(form, 'my_IdAssegnazione').value;
        const chiusuraEsercizio: string = getBaseSocialeControl(form, 'my_ChiusuraEsercizio').value;
        // VBS matches the AE_Incarico period whose stored date equals ChiusuraEsercizio+anno inserito (WHERE dataSoci/dataLav = data); no match → fields stay blank
        const targetDate = buildTargetDate(chiusuraEsercizio, my_Data);

        store
            .select(revisione53DatiSecondariSelectors.getAEIncaricoSoci(idAssegnazione))
            .pipe(take(1))
            .subscribe((AE_Incarico) => {
                const incarico = AE_Incarico.find((item) => item.dataSoci === targetDate);
                my_SociFisCoop.setValue(incarico?.sociFisiciCooperatori ?? '', { emitEvent: false });
                my_SociGiuCoop.setValue(incarico?.sociGiuridiciCooperatori ?? '', { emitEvent: false });
                my_SociFisFin.setValue(incarico?.sociFisiciFinanziatori ?? '', { emitEvent: false });
                my_SociGiuFin.setValue(incarico?.sociGiuridiciFinanziatori ?? '', { emitEvent: false });
                my_SociSpec.setValue(incarico?.sociSpeciali ?? '', { emitEvent: false });
                updateVerbaleOnStore(store, (myVerbale) => {
                    const myVerbaleClone = cloneDeep(myVerbale);
                    const sez = myVerbaleClone.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale;
                    sez[buildName('my_SociFisCoop')] = my_SociFisCoop.value;
                    sez[buildName('my_SociGiuCoop')] = my_SociGiuCoop.value;
                    sez[buildName('my_SociFisFin')] = my_SociFisFin.value;
                    sez[buildName('my_SociGiuFin')] = my_SociGiuFin.value;
                    sez[buildName('my_SociSpec')] = my_SociSpec.value;
                    return myVerbaleClone;
                });
            });

        store
            .select(revisione53DatiSecondariSelectors.getAEIncaricoLav(idAssegnazione))
            .pipe(take(1))
            .subscribe((AE_Incarico) => {
                const incarico = AE_Incarico.find((item) => item.dataLav === targetDate);
                my_UnitaSociUtil.setValue(incarico?.UnitaSociUtil ?? '', { emitEvent: false });
                my_UnitaNonSociUtil.setValue(incarico?.UnitaNonSociUtil ?? '', { emitEvent: false });
                my_NumLavSub.setValue(incarico?.NumLavSub ?? '', { emitEvent: false });
                my_NumLavSubTempoDeterminato.setValue(incarico?.NumLavSubTempoDeterminato ?? '', { emitEvent: false });
                my_NumLavParaSub.setValue(incarico?.NumLavParaSub ?? '', { emitEvent: false });
                my_NumLavAut.setValue(incarico?.NumLavAut ?? '', { emitEvent: false });
                my_NumLavAltro.setValue(incarico?.NumLavAltro ?? '', { emitEvent: false });
                updateVerbaleOnStore(store, (myVerbale) => {
                    const myVerbaleClone = cloneDeep(myVerbale);
                    const sez = myVerbaleClone.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale as Record<string, string>;
                    sez[buildName('my_UnitaSociUtil')] = my_UnitaSociUtil.value;
                    sez[buildName('my_UnitaNonSociUtil')] = my_UnitaNonSociUtil.value;
                    sez[buildName('my_NumLavSub')] = my_NumLavSub.value;
                    sez[buildName('my_NumLavSubTempoDeterminato')] = my_NumLavSubTempoDeterminato.value;
                    sez[buildName('my_NumLavParaSub')] = my_NumLavParaSub.value;
                    sez[buildName('my_NumLavAut')] = my_NumLavAut.value;
                    sez[buildName('my_NumLavAltro')] = my_NumLavAltro.value;
                    return myVerbaleClone;
                });
            });

        if (data2EsPrecControl) {
            const val = (toNum(my_Data) - 1).toString();
            data2EsPrecControl.setValue(val, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_EffettivitaBaseSociale.my_Data2EsPrec = val;
                return myVerbaleClone;
            });
        }
    });
};

// dataSoci_N/dataLav_N are stored as 'yyyy-mm-dd'; ChiusuraEsercizio holds 'dd-mm'/'dd/mm'
const buildTargetDate = (chiusuraEsercizio: string, anno: string): string | null => {
    const parts = (chiusuraEsercizio ?? '').split(/[/-]/);
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    if (isNilOrEmpty(anno) || isNaN(day) || isNaN(month)) return null;
    return `${anno}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
};
